import React from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useProjects } from "../../context/ProjectsContext";
import { normalizeProjectModules } from "../../utils/projectModules";
import { billingAccessLabel, getProjectBillingAccess } from "../../utils/projectBillingAccess";
import AffiliateSettingsCard from "./AffiliateSettingsCard";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const SETTINGS_SECTIONS = [
  {
    value: "general",
    label: "General",
    icon: <SettingsRoundedIcon />,
  },
  {
    value: "suscripcion",
    label: "Tu plan",
    icon: <WorkspacePremiumRoundedIcon />,
  },
  {
    value: "ecommerce",
    label: "Cobros de la tienda",
    icon: <PaymentsRoundedIcon />,
  },
  {
    value: "notificaciones",
    label: "Notificaciones",
    icon: <NotificationsActiveRoundedIcon />,
  },
  {
    value: "accesos",
    label: "Accesos",
    icon: <ManageAccountsRoundedIcon />,
  },
];

function currentSection(pathname) {
  if (pathname.includes("/configuracion/funcionalidades")) return "suscripcion";
  const match = SETTINGS_SECTIONS.find((section) =>
    pathname.includes(`/configuracion/${section.value}`),
  );
  return match?.value || "general";
}

export default function ProjectSettingsLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();

  const project = React.useMemo(() => {
    const matched = projects.find(
      (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
    );
    const activeMatches =
      activeProject?.slug === projectId ||
      activeProject?.id === projectId ||
      activeProject?._id === projectId;
    return matched || (activeMatches ? activeProject : null);
  }, [projects, projectId, activeProject]);

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    if (project && project.slug !== activeProject?.slug) setActiveProject(project);
  }, [project, activeProject?.slug, setActiveProject]);

  const modules = normalizeProjectModules(project?.modules);
  const activeModules = Object.values(modules).filter(Boolean).length;
  const billingAccess = getProjectBillingAccess(project);
  const billingChipColor = billingAccess.isFull
    ? "success"
    : billingAccess.isGrace
      ? "warning"
      : "error";
  const section = currentSection(location.pathname);

  const handleSectionChange = (_event, value) => {
    navigate(`/dashboard/proyectos/${projectId}/configuracion/${value}`);
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
      >
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
            Configuración
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
            {project?.name || projectId} · administrá tu negocio, tu plan y los accesos
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            icon={<WorkspacePremiumRoundedIcon />}
            label={`${activeModules} módulos activos`}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
          <Chip
            label={billingAccessLabel(billingAccess)}
            color={billingChipColor}
            variant="outlined"
            sx={{ fontWeight: 900 }}
          />
        </Stack>
      </Stack>

      <Card
        elevation={0}
        sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)", overflow: "hidden" }}
      >
        <Box sx={{ borderBottom: "1px solid rgba(25,36,47,0.08)", bgcolor: "#fff" }}>
          <Tabs
            value={section}
            onChange={handleSectionChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label="Secciones de configuración del proyecto"
            sx={{
              px: { xs: 1, md: 2 },
              minHeight: 62,
              "& .MuiTab-root": {
                minHeight: 62,
                textTransform: "none",
                fontWeight: 900,
                px: 2,
              },
              "& .Mui-selected": { color: `${BRAND.orange} !important` },
              "& .MuiTabs-indicator": { bgcolor: BRAND.orange, height: 3 },
            }}
          >
            {SETTINGS_SECTIONS.map((item) => (
              <Tab
                key={item.value}
                value={item.value}
                label={item.label}
                icon={item.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
        <CardContent sx={{ p: { xs: 2, md: 3.5 }, bgcolor: "#FBFBFD" }}>
          <Outlet />
        </CardContent>
      </Card>

      {section === "general" && <AffiliateSettingsCard />}
    </Stack>
  );
}
